import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Upload, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { formatSize } from './documents.utils';

interface FileDropZoneProps {
  accept: string;
  multiple?: boolean;
  maxSizeBytes: number;
  files: File[];
  onFilesChange: (files: File[]) => void;
  hint?: string;
  disabled?: boolean;
}

/** Drag & drop + native picker (camera on phones) with a client-side size pre-check. */
export const FileDropZone: React.FC<FileDropZoneProps> = ({ accept, multiple = false, maxSizeBytes, files, onFilesChange, hint, disabled }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);

  const addFiles = (incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    const tooLarge = list.filter((f) => f.size > maxSizeBytes).map((f) => f.name);
    const accepted = list.filter((f) => f.size <= maxSizeBytes);
    setRejected(tooLarge);
    onFilesChange(multiple ? [...files, ...accepted] : accepted.slice(0, 1));
  };

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          dragOver ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/20' : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/30',
          disabled && 'pointer-events-none opacity-60',
        )}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          addFiles(event.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(event) => {
            if (event.target.files) addFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">{t('documents.upload.dropHint', 'Trascina il file qui oppure tocca per selezionarlo')}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {hint ?? t('documents.upload.maxSize', 'Massimo {{size}}', { size: formatSize(maxSizeBytes) })}
        </p>
      </div>

      {rejected.length > 0 && (
        <p className="text-xs text-destructive">
          {t('documents.upload.tooLarge', 'File troppo grandi (max {{size}}): {{names}}', { size: formatSize(maxSizeBytes), names: rejected.join(', ') })}
        </p>
      )}

      {files.length > 0 && (
        <ul className="divide-y rounded-md border">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="flex items-center gap-2 px-3 py-2 text-sm">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{formatSize(file.size)}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('common.remove', 'Rimuovi')}
                onClick={() => onFilesChange(files.filter((_, i) => i !== index))}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
